import * as React from 'react';
import {
    AssetCommandDescriptors,
    getHashCode,
    useAssetCommands,
    useOpenUrlOnCtrlClick,
    usePatchPartialPayload,
    useTextChangeCallback,
} from 'owa-timestream-asset-sdk';
import { convertHotkeyStringToReadableText } from 'owa-readable-hotkeys';
import { enterUrl, clickToOpen, ctrlClickToOpen } from '../linkStrings.locstring.json';
import { KeyCodes } from '@fluentui/react/lib/Utilities';
import type { LinkPayload } from '../schema';
import { useIsMounted } from 'owa-react-hooks/lib/useIsMounted';
import { useOnBlurUpdate } from 'owa-react-hooks/lib/useOnBlurUpdate';
import { usePreviousUniqueValue } from 'owa-react-hooks/lib/usePreviousUniqueValue';
import getLinkPreviewOperation from 'owa-service/lib/operation/getLinkPreviewOperation';
import type LinkPreview from 'owa-service/lib/contract/LinkPreview';
import loc from 'owa-localize';
import normalizeUrl from './normalizeUrl';
import previewIsValid from './previewIsValid';
import useEditableLink from './useEditableLink';

export default function useLinkViewState({ url, title, linkPreview, errorMessage }: LinkPayload) {
    const isMounted = useIsMounted();
    const patchPartialPayload = usePatchPartialPayload<LinkPayload>();

    const [isLoading, setIsLoading] = React.useState(false);

    const operationId = React.useRef(0);
    const patchUrl = React.useCallback(
        (newUrl: string) => {
            const id = getHashCode(newUrl);
            operationId.current = id;
            setIsLoading(true);
            getLinkPreviewOperation({
                getLinkPreviewRequest: { Url: newUrl, Id: id },
            })
                .then(response => response)
                .catch(reason => ({
                    LinkPreview: undefined,
                    Error: undefined,
                    ErrorMessage: reason.message || JSON.stringify(reason),
                }))
                .then(response => {
                    if (isMounted.current) {
                        setIsLoading(false);

                        if (operationId.current == id) {
                            const errorCode = !!response.Error
                                ? parseInt(response.Error)
                                : undefined;
                            const responsePreview = response.LinkPreview as LinkPreview;

                            // Sanitize the link preview, since it can be a DocLinkPreview
                            // which contains a large base64-encoded preview image.
                            // Perhaps we should be upgrading this asset to a full File item.
                            // See https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/84727
                            const sanitizedLinkPreview: LinkPreview = responsePreview
                                ? {
                                      __type: responsePreview.__type,
                                      Id: responsePreview.Id,
                                      Url: responsePreview.Url,
                                      Title: responsePreview.Title,
                                      Description: responsePreview.Description,
                                      ImageUrl: responsePreview.ImageUrl,
                                  }
                                : { Id: id };

                            patchPartialPayload({
                                url: newUrl,
                                linkPreview: sanitizedLinkPreview,
                                errorMessage: response.ErrorMessage,
                                errorCode: errorCode,
                            });
                        }
                    }
                });
        },
        [isMounted, operationId, setIsLoading, patchPartialPayload]
    );

    const previousUrlValue = usePreviousUniqueValue(url);
    const [urlEditorValue, setUrlEditorValue] = React.useState(url);
    React.useEffect(
        function updateEditorValueWithNewUrlIfValueIsNotEdited() {
            if (url != previousUrlValue && urlEditorValue == previousUrlValue) {
                setUrlEditorValue(url);
            }
        },
        [url, previousUrlValue, urlEditorValue, setUrlEditorValue]
    );

    const urlEditorTooltip = urlEditorValue
        ? loc(ctrlClickToOpen, convertHotkeyStringToReadableText('CTRL'), urlEditorValue)
        : urlEditorValue;

    const onLinkBlur = React.useCallback(() => {
        const newUrl = normalizeUrl(urlEditorValue, false);
        setUrlEditorValue(newUrl);
        if (url != newUrl) {
            patchUrl(newUrl);
        }
    }, [url, patchUrl, urlEditorValue, setUrlEditorValue]);

    const onBlur = useOnBlurUpdate(onLinkBlur);

    const onKeyDown = React.useCallback(
        (e: React.KeyboardEvent) => {
            switch (e.keyCode) {
                case KeyCodes.enter:
                    e.preventDefault();
                    e.stopPropagation();
                    const newUrl = normalizeUrl(urlEditorValue, e.metaKey || e.ctrlKey);
                    setUrlEditorValue(newUrl);
                    patchUrl(newUrl);
                    break;
                case KeyCodes.escape:
                    setUrlEditorValue(url);
                    break;
            }
        },
        [url, patchUrl, urlEditorValue, setUrlEditorValue]
    );

    const patchTitle = React.useCallback(
        (title: string) => {
            patchPartialPayload({ title });
        },
        [patchPartialPayload]
    );

    const iconName = isLoading ? 'CloudDownload' : 'Link';
    const iconTitle = (isLoading ? '' : errorMessage) || '';
    const linkPreviewIsValid = previewIsValid({ url, linkPreview, errorMessage });
    const validLinkPreview = linkPreviewIsValid ? linkPreview! : ({} as LinkPreview);
    const effectiveLinkPreview = isLoading
        ? ({
              Title: title || '',
          } as LinkPreview)
        : {
              ...validLinkPreview,
              Url: url,
              Title: url ? title || validLinkPreview.Title || loc(clickToOpen) : '',
          };

    const [titleProps, onRename] = useEditableLink(effectiveLinkPreview.Title, patchTitle);

    useAssetCommands(
        [
            {
                ...AssetCommandDescriptors.openInNewWindow,
                disabled: !url,
                onInvoke: () => window.open(url),
            },
            {
                ...AssetCommandDescriptors.renameTitle,
                disabled: !url,
                onInvoke: onRename,
            },
        ],
        [url]
    );

    return {
        iconName,
        iconTitle,
        linkPreview: effectiveLinkPreview,
        titleProps,
        urlEditorProps: {
            placeholder: loc(enterUrl),
            title: urlEditorTooltip,
            value: urlEditorValue,
            onBlur,
            onChange: useTextChangeCallback(setUrlEditorValue),
            onClick: useOpenUrlOnCtrlClick(urlEditorValue),
            onKeyDown,
        },
    };
}
